<script setup>
import { watch, onBeforeUnmount } from "vue";
import { useDeployStore } from "@/stores/deploy";
import { useAuthStore } from "@/stores/auth";

// App-wide floating deploy indicator (a top-center pill, like Docs' "Saving…").
// Rendered once in App.vue so it overlays every route — including the
// fullscreen builder — without shifting any layout. Terminal states (done /
// untracked) auto-hide; a failure stays until dismissed.
const deploy = useDeployStore();
const auth = useAuthStore();

// The status endpoint needs a JWT — only (re)check once we know who's logged
// in. Catches deploys already running after a page refresh or from a colleague.
watch(() => auth.user, (u) => { if (u) deploy.init(); }, { immediate: true });

// "untracked" = rebuild dispatched but progress unfollowable (e.g. the GitHub
// token can't read workflow runs): keep showing "deploying" for the realistic
// build window instead of a premature ✓, then assume done and hide.
const AUTO_HIDE_MS = 6000;
const UNTRACKED_ASSUME_DONE_MS = 4 * 60 * 1000;
let hideTimer = null;
watch(() => deploy.state, (s) => {
  clearTimeout(hideTimer);
  if (s === "done") hideTimer = setTimeout(() => deploy.dismiss(), AUTO_HIDE_MS);
  if (s === "untracked") hideTimer = setTimeout(() => deploy.dismiss(), UNTRACKED_ASSUME_DONE_MS);
});
onBeforeUnmount(() => clearTimeout(hideTimer));
</script>

<template>
  <Transition
    enter-active-class="transition duration-200 ease-out"
    enter-from-class="opacity-0 -translate-y-2"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition duration-150 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 -translate-y-2"
  >
    <div
      v-if="deploy.state !== 'idle' && deploy.state !== 'unavailable'"
      class="fixed left-1/2 top-3 z-[100] -translate-x-1/2"
    >
      <div
        class="flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium shadow-lg backdrop-blur"
        :class="deploy.state === 'done'
          ? 'border-brand-green/30 bg-white/95 text-brand-700'
          : deploy.state === 'failed'
            ? 'border-rose-200 bg-rose-50/95 text-rose-700'
            : 'border-slate-200 bg-white/95 text-slate-700'"
      >
        <!-- pending / deploying / untracked: spinner (untracked is still a
             deploy in flight — we just can't follow it; never show a premature ✓) -->
        <svg
          v-if="deploy.state === 'pending' || deploy.state === 'deploying' || deploy.state === 'untracked'"
          class="h-4 w-4 animate-spin text-brand-green" fill="none" viewBox="0 0 24 24"
        >
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>

        <span v-if="deploy.state === 'pending'">已觸發網站更新，等待部署開始…</span>
        <span v-else-if="deploy.state === 'deploying'">網站更新中…（約需 2–3 分鐘）</span>
        <span v-else-if="deploy.state === 'done'">✓ 網站已更新{{ deploy.finishedAt ? ` ${deploy.finishedAt.toLocaleTimeString()}` : "" }}</span>
        <span v-else-if="deploy.state === 'untracked'">網站更新中…（約 3 分鐘後生效）</span>
        <template v-else-if="deploy.state === 'failed'">
          <span>網站更新失敗</span>
          <a v-if="deploy.runUrl" :href="deploy.runUrl" target="_blank" rel="noopener" class="underline">查看詳情</a>
        </template>

        <!-- terminal states are dismissible -->
        <button
          v-if="deploy.state === 'done' || deploy.state === 'failed' || deploy.state === 'untracked'"
          type="button"
          class="-mr-1 ml-1 rounded-full p-0.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          @click="deploy.dismiss()"
        >
          <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  </Transition>
</template>
